import React, { useEffect, useState } from "react";
import { matchQuery, getSortedCollection } from "sprint-common-web";

import { useAdminFramerateProfilesApi } from "../api/useAdminFramerateProfilesApi";
import { FramerateProfilesListingFilters } from "../listing/useFramerateProfilesListingFilters";
import { FramerateProfileListingContext } from "./FramerateProfilesListingContext";
import { FramerateProfileListingItem, mapToFramerateProfileListingItem } from "./FramerateProfilesListingItem";

export type FramerateProfilesListingProviderProps = React.PropsWithChildren<unknown>;

export const FramerateProfilesListingProvider: React.FC<FramerateProfilesListingProviderProps> = (
    props: FramerateProfilesListingProviderProps
) => {
    const [allItems, setAllItems] = useState<FramerateProfileListingItem[]>([]);
    const [items, setItems] = useState<FramerateProfileListingItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { getFramerateProfileList } = useAdminFramerateProfilesApi();

    useEffect(() => {
        setIsLoading(true);
        getFramerateProfileList()
            .then((response) => {
                const framerateProfileItems = response.items.map(mapToFramerateProfileListingItem);

                setAllItems(framerateProfileItems);
                setTotalItems(framerateProfileItems.length);
            })
            .finally(() => setIsLoading(false));
    }, []);

    const load = (filters: FramerateProfilesListingFilters): void => {
        const sortedAndFilteredItems = getSortedAndFilteredItems(allItems, filters);

        setTotalItems(sortedAndFilteredItems.length);
        setItems(sortedAndFilteredItems);
    };

    const addFramerateProfileItem = (item: FramerateProfileListingItem): void => {
        setAllItems((prevItems) => {
            return [
                {
                    id: item.id,
                    name: item.name,
                    mappings: item.mappings,
                    updatedDate: item.updatedDate,
                    isModified: true
                },
                ...prevItems
            ];
        });
    };

    const updateFramerateProfileItem = (item: FramerateProfileListingItem): void => {
        setAllItems((prevItems) => {
            const framerateProfileToUpdate = prevItems.find((framerateProfile) => framerateProfile.id === item.id);

            if (!framerateProfileToUpdate) return prevItems;

            framerateProfileToUpdate.name = item.name;
            framerateProfileToUpdate.mappings = item.mappings;
            framerateProfileToUpdate.updatedDate = item.updatedDate;
            framerateProfileToUpdate.isModified = true;

            return [...prevItems];
        });
    };

    return (
        <FramerateProfileListingContext.Provider
            value={{
                isLoading,
                totalItems,
                allItems,
                items,
                add: addFramerateProfileItem,
                update: updateFramerateProfileItem,
                load
            }}
        >
            {props.children}
        </FramerateProfileListingContext.Provider>
    );
};

const getSortedAndFilteredItems = (items: FramerateProfileListingItem[], filter: FramerateProfilesListingFilters) => {
    const filteredItems = items.filter((item: FramerateProfileListingItem) => matchQuery(item.name, filter.name));

    return getSortedCollection(filteredItems, filter.sortBy, filter.sortOrder);
};
